import type { DeviceOrder, DeviceOrderApi } from "../ports/DeviceOrders";

/**
 * Device Orders service.
 *
 * Maps snake_case wire shapes ↔ camelCase UI shapes and wraps everything in
 * `IControllerPromiseResponse<T, { message }>`.
 *
 * NOTE: `apiProvider` swallows Axios errors and returns the error *body* rather
 * than throwing (see apiProvider.ts). So we cannot rely on try/catch alone — we
 * validate the response shape and surface a `{ success: false }` with the
 * backend's error message when the expected fields are missing.
 */

const toDeviceOrder = (api: DeviceOrderApi): DeviceOrder => ({
  id: String(api.id),
  clinicId: api.clinic_id,
  patientId: api.patient_id ?? null,
  status: api.status,
  createdAt: api.created_at,
  recipientName: api.recipient_name,
  recipientAddress: api.recipient_address,
  instructions: api.instructions ?? "",
  trackerUrl: api.tracker_url ?? "",
});

const errorMessage = (response: unknown, fallback: string): string => {
  const body = response as
    | { message?: string; detail?: { message?: string } | string }
    | undefined;
  if (typeof body?.detail === "object" && body?.detail?.message) {
    return body.detail.message;
  }
  if (typeof body?.detail === "string") {
    return body.detail;
  }
  return body?.message ?? fallback;
};

const isDeviceOrderApi = (value: unknown): value is DeviceOrderApi =>
  !!value &&
  typeof value === "object" &&
  typeof (value as DeviceOrderApi).id === "number";

export const deviceOrdersServiceImplFactory = ({
  deviceOrdersRepository,
}: {
  deviceOrdersRepository: IDeviceOrdersRepository;
}): IDeviceOrdersService => {
  const toErr = (message: string) => ({ message });

  const list: IListDeviceOrdersService = async ({ clinicId }) => {
    try {
      const response = await deviceOrdersRepository.list({
        clinic_id: clinicId,
      });
      if (!response || !Array.isArray(response.orders)) {
        return {
          success: false,
          error: toErr(errorMessage(response, "Failed to load device orders")),
        };
      }
      return { success: true, data: response.orders.map(toDeviceOrder) };
    } catch (error) {
      return {
        success: false,
        error: toErr(
          (error as Error)?.message ?? "Failed to load device orders"
        ),
      };
    }
  };

  const create: ICreateDeviceOrderService = async ({
    clinicId,
    recipientName,
    recipientAddress,
    instructions,
    trackerUrl,
    patientId,
    status,
  }) => {
    try {
      const response = await deviceOrdersRepository.create({
        clinic_id: clinicId,
        recipient_name: recipientName,
        recipient_address: recipientAddress,
        instructions: instructions ?? null,
        tracker_url: trackerUrl ?? null,
        patient_id: patientId ?? null,
        status,
      });
      if (!isDeviceOrderApi(response)) {
        return {
          success: false,
          error: toErr(errorMessage(response, "Failed to create device order")),
        };
      }
      return { success: true, data: toDeviceOrder(response) };
    } catch (error) {
      return {
        success: false,
        error: toErr(
          (error as Error)?.message ?? "Failed to create device order"
        ),
      };
    }
  };

  const update: IUpdateDeviceOrderService = async ({
    clinicId,
    orderId,
    recipientName,
    recipientAddress,
    instructions,
    trackerUrl,
    patientId,
    status,
  }) => {
    try {
      const response = await deviceOrdersRepository.update({
        clinic_id: clinicId,
        order_id: Number(orderId),
        ...(recipientName !== undefined
          ? { recipient_name: recipientName }
          : {}),
        ...(recipientAddress !== undefined
          ? { recipient_address: recipientAddress }
          : {}),
        ...(instructions !== undefined ? { instructions } : {}),
        ...(trackerUrl !== undefined ? { tracker_url: trackerUrl } : {}),
        ...(patientId !== undefined ? { patient_id: patientId } : {}),
        ...(status !== undefined ? { status } : {}),
      });
      if (!isDeviceOrderApi(response)) {
        return {
          success: false,
          error: toErr(errorMessage(response, "Failed to update device order")),
        };
      }
      return { success: true, data: toDeviceOrder(response) };
    } catch (error) {
      return {
        success: false,
        error: toErr(
          (error as Error)?.message ?? "Failed to update device order"
        ),
      };
    }
  };

  const remove: IDeleteDeviceOrderService = async ({ clinicId, orderId }) => {
    try {
      const response = await deviceOrdersRepository.remove({
        clinic_id: clinicId,
        order_id: Number(orderId),
      });
      if (!response || response.deleted !== true) {
        return {
          success: false,
          error: toErr(errorMessage(response, "Failed to delete device order")),
        };
      }
      return { success: true, data: response };
    } catch (error) {
      return {
        success: false,
        error: toErr(
          (error as Error)?.message ?? "Failed to delete device order"
        ),
      };
    }
  };

  return {
    list,
    create,
    update,
    remove,
  };
};
